import { BaseElement } from '../../../base-element';
import type { ProcessingDto, NotificationStatus, PdfStatus } from '../../../../api/types';
import '../../../ui/dt-card';
import '../../../ui/dt-badge';

interface BadgeConfig {
  variant: 'success' | 'warning' | 'error' | 'info';
  label: string;
}

export class ProcessingStatusCard extends BaseElement {
  private _processing: ProcessingDto | null = null;

  get processing(): ProcessingDto | null {
    return this._processing;
  }

  set processing(value: ProcessingDto | null) {
    this._processing = value;
    if (this.isConnected) {
      this.updateRender();
    }
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private getPdfBadge(status: PdfStatus): BadgeConfig {
    switch (status) {
      case 'Success':
        return { variant: 'success', label: 'Ready' };
      case 'Failed':
        return { variant: 'error', label: 'Failed' };
      case 'InProgress':
        return { variant: 'warning', label: 'Processing' };
      case 'Queued':
      default:
        return { variant: 'info', label: 'Queued' };
    }
  }

  private getNotificationBadge(status: NotificationStatus): BadgeConfig {
    switch (status) {
      case 'Success':
        return { variant: 'success', label: 'Sent' };
      case 'Failed':
        return { variant: 'error', label: 'Failed' };
      case 'InProgress':
        return { variant: 'warning', label: 'Processing' };
      case 'Queued':
      default:
        return { variant: 'info', label: 'Queued' };
    }
  }

  private formatDate(value?: string | null): string {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  private updateRender(): void {
    if (!this._processing) {
      this.render('');
      return;
    }

    const pdfBadge = this.getPdfBadge(this._processing.pdfStatus as PdfStatus);

    this.render(`
      <dt-card variant="outlined" data-testid="processing-card">
        <div slot="header" class="text-lg font-semibold text-neutral-900">Processing Status</div>
        <div class="flex flex-col gap-4">
          <div class="flex flex-wrap items-center gap-3">
            <span class="text-sm font-semibold text-neutral-700">PDF</span>
            <dt-badge variant="${pdfBadge.variant}">${pdfBadge.label}</dt-badge>
          </div>
          ${this._processing.lastError?.message ? `
            <div class="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-700" data-testid="processing-error">
              ${this._processing.lastError.message}
            </div>
          ` : ''}
          <div class="flex flex-col gap-2">
            <div class="text-sm font-semibold text-neutral-700">Email Notifications</div>
            <div class="flex flex-col gap-2">
              ${this._processing.emailNotifications.map((notification) => {
                const badge = this.getNotificationBadge(notification.status as NotificationStatus);
                const sentAt = this.formatDate(notification.sentAtUtc);
                return `
                  <div class="flex flex-wrap items-center gap-3 rounded-lg border border-neutral-200 px-3 py-2 text-sm">
                    <span class="font-semibold text-neutral-800">${notification.recipientType}</span>
                    <dt-badge variant="${badge.variant}">${badge.label}</dt-badge>
                    ${sentAt ? `<span class="text-xs text-neutral-500">${sentAt}</span>` : ''}
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        </div>
      </dt-card>
    `);
  }
}

customElements.define('dt-processing-status-card', ProcessingStatusCard);
